import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { classifyLoginError, LoginFailure } from '../utils/loginFailure';
import { getRedirectDecision, redirectToPortal } from '../utils/portalRedirect';
import PortalRedirectModal from './PortalRedirectModal';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<LoginFailure | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [pendingUserData, setPendingUserData] = useState<any>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Normalize email to lowercase before sending
      const userData = await login(email.toLowerCase().trim(), password);
      
      // Normalize roles to strings (they might be Role objects)
      const roles = (userData.roles || []).map((role: any) => 
        typeof role === 'string' ? role : role.name
      );
      
      // Check if user should be redirected to a different portal
      const decision = getRedirectDecision(roles, 'hub');
      
      if (decision.shouldRedirect && decision.targetPortal && userData.token) {
        // Redirect to the appropriate portal
        redirectToPortal(decision.targetPortal, userData.token, userData);
        return; // Prevent navigation, redirect will handle it
      }
      
      if (decision.showPrompt) {
        // Show modal for admin-only users
        setPendingUserData(userData);
        setShowRedirectModal(true);
        setIsLoading(false);
        return;
      }
      
      // User can access hub portal, proceed normally
      navigate('/dashboard');
    } catch (err: unknown) {
      setError(classifyLoginError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueToHub = () => {
    setShowRedirectModal(false);
    navigate('/dashboard');
  };

  const handleCloseModal = () => {
    setShowRedirectModal(false);
    setPendingUserData(null);
  };

  const goToReset = () => {
    navigate('/forgot-password', { state: { email: email.toLowerCase().trim() } });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <img
            className="mx-auto h-24 w-auto"
            src="/logo.png"
            alt="242Network"
          />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            242Network Hub
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to access the staff dashboard
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4" role="alert">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800">{error.title}</h3>
                  <p className="mt-1 text-sm text-red-700">{error.message}</p>
                  {error.showReset && (
                    <button
                      type="button"
                      onClick={goToReset}
                      className="mt-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-medium text-primary-700 shadow-sm ring-1 ring-inset ring-red-200 hover:bg-red-100"
                    >
                      Reset your password
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                className={`appearance-none rounded-none relative block w-full px-3 py-2 border ${
                  error?.kind === 'credentials' ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                placeholder="Email address"
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                className={`appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border ${
                  error?.kind === 'credentials' ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center z-20"
              >
                {showPassword ? (
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <button
                type="button"
                onClick={goToReset}
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Portal Redirect Modal */}
      {pendingUserData && (
        <PortalRedirectModal
          isOpen={showRedirectModal}
          onClose={handleCloseModal}
          onContinueToHub={handleContinueToHub}
          token={pendingUserData.token}
          userData={pendingUserData}
        />
      )}
    </div>
  );
};

export default LoginForm;
